import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { fundingRateIsCurrent } from '../amount/fx.js';
import { useFundingProfileQuote } from '../customer/funding-profile-react.js';
import { fundingSettlement } from '../discovery/settlement-asset.js';
import { FundingCurrencyError, unavailableFundingFx } from '../operations/currency-diagnostics.js';
import { providerFailure } from '../provider-error.js';
import { fetchFundingQuote } from './fetch-funding-quote.js';
export function useFundingQuotePreview(provider, intent, enabled, t0, diagnose) {
  const $ = _c(26);
  const contextKey = t0 === undefined ? "" : t0;
  const [bounds, setBounds] = useState(_temp);
  const scope = intent ? JSON.stringify([contextKey, intent.destination, intent.receiveTarget?.fx.source, intent.receiveTarget?.fx.observedAt, intent.fiatCurrency, intent.fiatDecimals, intent.method, intent.providerPaymentMethodId, intent.settlement.provider_asset_code]) : "";
  const cachedBound = bounds.get(scope);
  const minimum = cachedBound && cachedBound.expiresAt > Date.now() ? cachedBound.failure : undefined;
  useEffect(() => {
    if (!cachedBound) {
      return;
    }
    const timer = setTimeout(() => setBounds(previous => {
      const next = new Map(previous);
      next.delete(scope);
      return next;
    }), Math.max(1, cachedBound.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [cachedBound, scope]);
  const belowMinimum = Boolean(intent && minimum?.amountBoundary && BigInt(intent.principalUnits) < BigInt(minimum.amountBoundary.amountUnits));
  const noticeKey = enabled && belowMinimum && intent ? JSON.stringify([scope, intent.principalUnits, minimum?.amountBoundary?.amountUnits]) : null;
  const [settledNotice, setSettledNotice] = useState(null);
  let t1;
  let t2;
  if ($[0] !== noticeKey) {
    t1 = () => {
      if (!noticeKey) {
        setSettledNotice(null);
        return;
      }
      setSettledNotice(null);
      const timer_0 = setTimeout(() => setSettledNotice(noticeKey), 800);
      return () => clearTimeout(timer_0);
    };
    t2 = [noticeKey];
    $[0] = noticeKey;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== belowMinimum || $[4] !== enabled || $[5] !== intent) {
    t3 = enabled && intent && !belowMinimum ? JSON.stringify(intent) : null;
    $[3] = belowMinimum;
    $[4] = enabled;
    $[5] = intent;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const key = t3;
  const [revision, setRevision] = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setRevision(_temp2);
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const retry = t4;
  let t5;
  let t6;
  if ($[8] !== diagnose || $[9] !== key || $[10] !== provider || $[11] !== revision || $[12] !== scope) {
    t5 = () => {
      if (!key) {
        setSnapshot(null);
        return;
      }
      const controller = new AbortController();
      const current_0 = JSON.parse(key);
      setSnapshot(previous_0 => ({
        key,
        revision,
        value: previous_0?.key === key ? {
          ...previous_0.value,
          state: "loading",
          providerFailure: null,
          error: null
        } : empty("loading", current_0.principalUnits)
      }));
      let expiry;
      const debounce = setTimeout(() => {
        fetchFundingQuote(provider, current_0, controller.signal, undefined, diagnose, current_0.method === "card" && !current_0.providerPaymentMethodId ? "indicative" : "execution").then(t7 => {
          const {
            quote,
            review
          } = t7;
          if (controller.signal.aborted) {
            return;
          }
          if (review.state === "expired") {
            throw new Error("quote_expired");
          }
          setSnapshot({
            key,
            revision,
            value: {
              state: "ready",
              amountUnits: current_0.principalUnits,
              quote,
              review,
              providerFailure: null,
              error: null
            }
          });
          expiry = setTimeout(retry, Math.max(1, Math.min(Date.parse(quote.expiresAt) - Date.now(), 2147483647)));
        }).catch(error => {
          if (controller.signal.aborted) {
            return;
          }
          const failure = providerFailure(error);
          if (failure?.amountBoundary?.kind === "minimum") {
            setBounds(previous_1 => new Map(previous_1).set(scope, {
              failure,
              expiresAt: Date.now() + 1800000
            }));
          }
          setSnapshot(previous_2 => ({
            key,
            revision,
            value: {
              ...(previous_2?.key === key ? previous_2.value : empty("error", current_0.principalUnits)),
              state: "error",
              providerFailure: failure,
              error: failure?.category ?? (error instanceof FundingCurrencyError ? error.message : null) ?? (error instanceof Error && ["quote_expired", "funding_fx_unavailable", "onramp_quote_target_mismatch", "onramp_quote_intent_mismatch", "onramp_quote_fee_currency_mismatch", "onramp_card_binding_invalid"].includes(error.message) ? error.message : "provider_unavailable")
            }
          }));
        });
      }, 800);
      return () => {
        clearTimeout(debounce);
        if (expiry) {
          clearTimeout(expiry);
        }
        controller.abort();
      };
    };
    t6 = [key, provider, revision, retry, scope, diagnose];
    $[8] = diagnose;
    $[9] = key;
    $[10] = provider;
    $[11] = revision;
    $[12] = scope;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useEffect(t5, t6);
  let t7;
  if ($[15] !== belowMinimum || $[16] !== enabled || $[17] !== intent?.principalUnits || $[18] !== key || $[19] !== minimum || $[20] !== revision || $[21] !== snapshot) {
    t7 = enabled && belowMinimum ? {
      ...empty("idle", intent?.principalUnits ?? null),
      providerFailure: minimum ?? null
    } : key && snapshot?.key === key ? snapshot.revision === revision ? snapshot.value : {
      ...snapshot.value,
      state: "loading",
      providerFailure: null,
      error: null
    } : empty(key ? "loading" : "idle", intent?.principalUnits ?? null);
    $[15] = belowMinimum;
    $[16] = enabled;
    $[17] = intent?.principalUnits;
    $[18] = key;
    $[19] = minimum;
    $[20] = revision;
    $[21] = snapshot;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const current_1 = t7;
  const t8 = noticeKey && settledNotice === noticeKey ? minimum?.amountBoundary?.amountUnits ?? null : null;
  let t9;
  if ($[23] !== current_1 || $[24] !== t8) {
    t9 = {
      ...current_1,
      minimumNoticeUnits: t8,
      retry
    };
    $[23] = current_1;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function _temp2(current) {
  return current + 1;
}
function _temp() {
  return new Map();
}
function empty(state, amountUnits) {
  return {
    state,
    amountUnits,
    quote: null,
    review: null,
    providerFailure: null,
    error: null
  };
}
export function canContinueFundingAmount(units, preview, needsVerification) {
  return Boolean(units && /^[1-9]\d{0,19}$/.test(units) && (needsVerification || preview.amountUnits === units && preview.state === 'ready' && preview.quote && Date.parse(preview.quote.expiresAt) > Date.now() && (preview.review?.state === 'ready' || preview.review?.state === 'challenge_required')));
}
export function useFundingQuoteExpiry(quote, enabled, setReview, refresh) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const refreshed = useRef(null);
  useEffect(() => {
    if (!quote || !enabled || refreshed.current === quote) return;
    const delay = Math.max(0, Math.min(Date.parse(quote.expiresAt) - Date.now(), 2147483647));
    const timer = setTimeout(() => {
      refreshed.current = quote;
      setReview(current => current ? {
        ...current,
        state: 'expired'
      } : null);
      refreshRef.current();
    }, delay);
    return () => clearTimeout(timer);
  }, [quote, enabled, setReview]);
}
export function useFundingCheckoutPreview(t0) {
  const $ = _c(51);
  const {
    options,
    method,
    amountUnits,
    discovery,
    state,
    busy,
    refreshing,
    canUpdateAmount,
    providerPaymentMethodId
  } = t0;
  const {
    client,
    provider,
    currency,
    decimals
  } = options;
  const walletConnectionRequired = (method === "apple_pay" || method === "google_pay") && discovery?.status === "connectionRequired" && !discovery.guest;
  let t1;
  if ($[0] !== amountUnits || $[1] !== currency || $[2] !== decimals || $[3] !== discovery || $[4] !== method || $[5] !== options.directPurchase || $[6] !== options.fundingFx || $[7] !== providerPaymentMethodId) {
    t1 = method && amountUnits && /^[1-9]\d{0,19}$/.test(amountUnits) && discovery?.context.destination ? {
      destination: discovery.context.destination,
      method,
      ...(providerPaymentMethodId ? {
        providerPaymentMethodId
      } : {}),
      fiatCurrency: currency,
      fiatDecimals: decimals,
      principalUnits: amountUnits,
      ...(!options.directPurchase && options.fundingFx && discovery.context.asset === "USDC_SOL" ? {
        receiveTarget: {
          localUnits: amountUnits,
          fx: options.fundingFx
        }
      } : {}),
      settlement: options.directPurchase ? {
        provider_asset_code: options.directPurchase.binding.providerAssetCode,
        decimals: options.directPurchase.binding.decimals,
        caip19: options.directPurchase.binding.caip19
      } : fundingSettlement(discovery.context.asset)
    } : null;
    $[0] = amountUnits;
    $[1] = currency;
    $[2] = decimals;
    $[3] = discovery;
    $[4] = method;
    $[5] = options.directPurchase;
    $[6] = options.fundingFx;
    $[7] = providerPaymentMethodId;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const previewIntent = t1;
  let t2;
  if ($[9] !== currency || $[10] !== options.directPurchase || $[11] !== options.fundingFx) {
    t2 = options.directPurchase !== undefined || options.fundingFx === undefined || Boolean(options.fundingFx && fundingRateIsCurrent(options.fundingFx, Date.now()) && options.fundingFx.ratesD9[currency]);
    $[9] = currency;
    $[10] = options.directPurchase;
    $[11] = options.fundingFx;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const valuationReady = t2;
  let t3;
  if ($[13] !== client.cacheKey || $[14] !== discovery?.context || $[15] !== discovery?.customerVerification?.customerId || $[16] !== options.sessionProofId) {
    t3 = JSON.stringify([client.cacheKey, options.sessionProofId, discovery?.customerVerification?.customerId, discovery?.context.country, discovery?.context.subdivision]);
    $[13] = client.cacheKey;
    $[14] = discovery?.context;
    $[15] = discovery?.customerVerification?.customerId;
    $[16] = options.sessionProofId;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const providerPreview = useFundingQuotePreview(provider, previewIntent, (!options.directPurchase || options.directPurchaseEnabled === true) && state === "amount" && !(options.directPurchase && discovery?.status === "connectionRequired") && !walletConnectionRequired && valuationReady && !refreshing && options.active !== false && (method === "card" || !discovery?.needsContactVerification), t3, client.observeDiagnostic);
  let t4;
  let t5;
  if ($[18] !== client || $[19] !== currency || $[20] !== decimals || $[21] !== options.active || $[22] !== options.fundingFx || $[23] !== options.fundingFxLoading || $[24] !== valuationReady) {
    t4 = () => {
      if (!valuationReady && !options.fundingFxLoading && options.active !== false) {
        client.observeDiagnostic?.(unavailableFundingFx(currency, decimals, options.fundingFx, Date.now()));
      }
    };
    t5 = [client, currency, decimals, valuationReady, options.fundingFx, options.fundingFxLoading, options.active];
    $[18] = client;
    $[19] = currency;
    $[20] = decimals;
    $[21] = options.active;
    $[22] = options.fundingFx;
    $[23] = options.fundingFxLoading;
    $[24] = valuationReady;
    $[25] = t4;
    $[26] = t5;
  } else {
    t4 = $[25];
    t5 = $[26];
  }
  useEffect(t4, t5);
  let t6;
  if ($[27] !== options.fundingFxLoading || $[28] !== providerPreview || $[29] !== valuationReady) {
    t6 = valuationReady ? providerPreview : {
      ...providerPreview,
      state: options.fundingFxLoading ? "loading" : "error",
      error: options.fundingFxLoading ? null : "funding_fx_unavailable",
      quote: null,
      review: null
    };
    $[27] = options.fundingFxLoading;
    $[28] = providerPreview;
    $[29] = valuationReady;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  const preview = t6;
  useFundingProfileQuote(client, options.sessionProofId, previewIntent, preview.review);
  let t7;
  let t8;
  if ($[31] !== client || $[32] !== method || $[33] !== preview.error) {
    t7 = () => {
      if (preview.error && method) {
        client.observeFunnel?.({
          stage: method === "card" ? "card_quote" : "amount",
          outcome: "error",
          method,
          errorCode: preview.error
        });
      }
    };
    t8 = [client, method, preview.error];
    $[31] = client;
    $[32] = method;
    $[33] = preview.error;
    $[34] = t7;
    $[35] = t8;
  } else {
    t7 = $[34];
    t8 = $[35];
  }
  useEffect(t7, t8);
  let t9;
  if ($[36] !== amountUnits || $[37] !== busy || $[38] !== canUpdateAmount || $[39] !== discovery?.context || $[40] !== discovery?.needsContactVerification || $[41] !== discovery?.status || $[42] !== method || $[43] !== options.directPurchase || $[44] !== preview || $[45] !== valuationReady || $[46] !== walletConnectionRequired) {
    t9 = valuationReady && !busy && canUpdateAmount && Boolean(discovery?.context.destination) && canContinueFundingAmount(amountUnits, preview, walletConnectionRequired || method !== "card" && (discovery?.needsContactVerification ?? false) || Boolean(options.directPurchase && (method === "card" || discovery?.status === "connectionRequired")));
    $[36] = amountUnits;
    $[37] = busy;
    $[38] = canUpdateAmount;
    $[39] = discovery?.context;
    $[40] = discovery?.needsContactVerification;
    $[41] = discovery?.status;
    $[42] = method;
    $[43] = options.directPurchase;
    $[44] = preview;
    $[45] = valuationReady;
    $[46] = walletConnectionRequired;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  const canContinueAmount = t9;
  const cardEstimateReady = method === "card" && valuationReady && !busy && canUpdateAmount && preview.state === "ready" && preview.amountUnits === amountUnits && preview.review?.state === "indicative" && Boolean(preview.quote && Date.parse(preview.quote.expiresAt) > Date.now());
  const t10 = canContinueAmount || cardEstimateReady;
  let t11;
  if ($[48] !== preview || $[49] !== t10) {
    t11 = {
      preview,
      canContinueAmount: t10
    };
    $[48] = preview;
    $[49] = t10;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  return t11;
}